import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Container, Media, Spinner } from 'reactstrap';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import qs from 'qs';
import SimpleCard from '../../../../utils/card/SimpleCard';
import currencyFormatter from '../../../../utils/currencyFormatter';
import { getDocs } from '../../../../utils/fc';
import oikosApi from '../../../../services/oikos';
import { useFinanceSectorUser } from '../../../../utils/setorFinanceiro';
import { OIKOS_FILES_BASE, TP_DOC_NF_DANFE, TP_DOC_NF_XML } from '../../../../constants/oikosReceitas';
import { diffDays, fmtDateBr, inRange, isFaturado, isRecebido, monthRefToRange, num, parseDate, startOfDay, startOfToday } from './utils';

const computeCardMetrics = (recs) => {
  const out = { total: 0, valorTotalLiq: 0, valorTotalBru: 0 };
  out.total = Array.isArray(recs) ? recs.length : 0;
  if (!Array.isArray(recs) || recs.length === 0) return out;
  for (const r of recs) {
    out.valorTotalLiq += num(r?.rec_vlrLiq);
    out.valorTotalBru += num(r?.rec_vlrBru);
  }
  return out;
};

const StatLines = ({ recs, subtitle }) => {
  const m = useMemo(() => computeCardMetrics(recs), [recs]);
  return (
    <div className="d-flex flex-column" style={{ gap: 6 }}>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{currencyFormatter.format(m.valorTotalLiq)}</div>
      <div style={{ fontSize: 12, color: '#6c757d' }}>
        <span style={{ fontWeight: 700, color: '#4e515e' }}>{m.total}</span> itens
        {subtitle ? <span style={{ marginLeft: 8 }}>{subtitle}</span> : null}
      </div>
      <div style={{ fontSize: 11, color: '#6c757d' }}>Bruto: {currencyFormatter.format(m.valorTotalBru)}</div>
    </div>
  );
};

const MetricCard = ({ title, recs, onClick, className, selected, subtitle }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className}
      style={{ width: '100%' }}
      role="button"
      tabIndex={0}
      aria-pressed={!!selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (typeof onClick === 'function') onClick();
        }
      }}
    >
      <Card
        style={{
          transition: 'border 0.18s ease, box-shadow 0.18s ease',
          border: selected ? '2px solid #4e515e' : hovered ? '2px solid #505A62' : '2px solid transparent',
          boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.10)' : undefined,
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontWeight: 'bold' }} className="text-truncate font-size-14 mb-2">
                  {title}
                </span>
              </div>
              <StatLines recs={recs} subtitle={subtitle} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const getRecebimentoDueDate = (rec) => parseDate(rec?.rec_dtVenc) || parseDate(rec?.rec_dtQuitAtual);
const getRecebimentoForecastDate = (rec) => parseDate(rec?.rec_dtQuitAtual);
const inadimplenciaCutoff = parseDate('2024-01-01');

const isRecebimentoEmAtraso = (rec, today) => {
  const due = getRecebimentoDueDate(rec);
  if (!due) return false;
  return diffDays(due, today) < 0;
};

const getDiasAtraso = (rec, today) => {
  const due = getRecebimentoDueDate(rec);
  if (!due) return null;
  return diffDays(today, due); // today - due (positivo = vencido)
};

const getRecebSituacao = (rec, today) => {
  if (isRecebido(rec)) return 'recebido';
  const due = getRecebimentoDueDate(rec);
  if (!due) return 'sem_data';
  if (due < startOfDay(today)) return 'atraso';
  return 'aberto';
};

const situacaoLabel = (key) => {
  if (key === 'recebido') return 'Recebido';
  if (key === 'atraso') return 'Em atraso';
  if (key === 'aberto') return 'Em aberto';
  if (key === 'sem_data') return 'Sem data';
  return key || '-';
};

const Pill = ({ value }) => {
  const bg = value === 'recebido' ? '#1cbb8c' : value === 'atraso' ? '#ff5757' : value === 'aberto' ? '#4e515e' : '#a0aec0';
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, color: '#fff', backgroundColor: bg, whiteSpace: 'nowrap' }}>
        {situacaoLabel(value)}
      </span>
    </div>
  );
};

const DetailsCard = ({ rec }) => {
  const podeVerNf = useFinanceSectorUser();
  const [nfModalOpen, setNfModalOpen] = useState(false);
  const [nfLoading, setNfLoading] = useState(false);
  const [nfError, setNfError] = useState(null);
  const [nf, setNf] = useState(null);
  const [nfId, setNfId] = useState(rec?.nf_id ?? null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docs, setDocs] = useState([]);

  const recNfId = rec?.nf_id ?? null;

  useEffect(() => {
    setNfModalOpen(false);
  }, [rec?.rec_id]);

  useEffect(() => {
    let cancelled = false;
    const fetchNf = async () => {
      if (!podeVerNf || !recNfId) {
        setNf(null);
        setNfError(null);
        setNfLoading(false);
        return;
      }
      setNfLoading(true);
      setNfError(null);
      try {
        const res = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: recNfId, nf_ativo: 1 } })}`);
        if (cancelled) return;
        const row = Array.isArray(res?.data) ? res.data[0] : null;
        if (!row) {
          setNf(null);
          setNfError(`NF não encontrada para nf_id=${recNfId}.`);
          return;
        }
        setNf(row);
      } catch (e) {
        if (cancelled) return;
        setNf(null);
        setNfError(e?.message || 'Erro ao buscar a Nota Fiscal.');
      } finally {
        if (!cancelled) setNfLoading(false);
      }
    };
    fetchNf();
    return () => {
      cancelled = true;
    };
  }, [recNfId, podeVerNf]);

  useEffect(() => {
    setNfId(recNfId);
  }, [recNfId]);

  useEffect(() => {
    let cancelled = false;
    const loadDocs = async () => {
      if (!podeVerNf || !nfId) {
        setDocs([]);
        setDocsError(null);
        setDocsLoading(false);
        return;
      }
      setDocsLoading(true);
      setDocsError(null);
      try {
        const res = await getDocs('nf', nfId);
        if (cancelled) return;
        setDocs(Array.isArray(res) ? res : []);
      } catch (e) {
        if (cancelled) return;
        setDocs([]);
        setDocsError(e?.message || 'Erro ao buscar anexos da NF.');
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    };
    loadDocs();
    return () => {
      cancelled = true;
    };
  }, [nfId, podeVerNf]);

  if (!rec) return null;

  const today = startOfToday();
  const forecast = getRecebimentoForecastDate(rec);
  const dias = getDiasAtraso(rec, today);
  const situacao = getRecebSituacao(rec, today);

  const LabelValue = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 6, minWidth: 0 }}>
      <span style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>{label}:</span>
      <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value ?? '-'}</span>
    </div>
  );

  const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-');
  const formatDateOnly = (v) => (v ? fmtDateBr(v) : '-');
  const formatMoney = (v) => currencyFormatter.format(num(v));
  const formatPercent = (v) => `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num(v))}%`;

  const getLatestDocByTp = (tpId) => {
    const filtered = (docs || []).filter((d) => Number(d?.tpDocumento_id) === Number(tpId));
    if (filtered.length === 0) return null;
    filtered.sort((a, b) => Number(b?.arquivo_id || 0) - Number(a?.arquivo_id || 0));
    return filtered[0];
  };

  const xmlDoc = getLatestDocByTp(TP_DOC_NF_XML);
  const pdfDoc = getLatestDocByTp(TP_DOC_NF_DANFE);

  const openDoc = (doc) => {
    const path = doc?.arquivo_caminho;
    if (!path) return;
    window.open(`${OIKOS_FILES_BASE}${path}`, '_blank');
  };

  return (
    <div className="ml-4" style={{ width: '100%' }}>
      <SimpleCard
        standard={false}
        title={<h4 className="text-truncate font-size-14 mb-2">{`Detalhes: ${rec.rec_ep || 'EP'}`}</h4>}
        text={
          <div className="d-flex flex-column" style={{ gap: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#6c757d', fontSize: 12 }}>Projeto</div>
                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.recProjeto || '-'}</div>
              </div>
              <Pill value={situacao} />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div>
                Vencimento: <b>{fmtDateBr(rec.rec_dtVenc || rec.rec_dtQuitAtual)}</b>
              </div>
              {forecast && (
                <div style={{ color: '#6c757d' }}>
                  Quitação (prev.): <b>{fmtDateBr(rec.rec_dtQuitAtual)}</b>
                </div>
              )}
              {dias != null && !isRecebido(rec) && (
                <div style={{ color: dias > 0 ? '#ff5757' : '#6c757d' }}>
                  {dias > 0 ? `Atraso: ${dias}d` : `Em ${Math.abs(dias)}d`}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div>
                Líquido: <b>{currencyFormatter.format(num(rec.rec_vlrLiq))}</b>
              </div>
              <div>
                Bruto: <b>{currencyFormatter.format(num(rec.rec_vlrBru))}</b>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div>
                Faturado: <b>{isFaturado(rec) ? 'Sim' : 'Não'}</b>
              </div>
              <div>
                Risco: <b>{rec.rec_risco ?? '-'}</b>
              </div>
              <div>
                rec_id: <b>{rec.rec_id}</b>
              </div>
            </div>

            {podeVerNf && (
            <>
            <hr className="my-2" />

            <div className="d-flex flex-column" style={{ gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 260 }}>
                  <div style={{ fontWeight: 800 }}>Nota Fiscal</div>
                  <div style={{ fontWeight: 900, color: '#4e515e' }}>{nf?.nf_num || (nfLoading ? 'Carregando...' : '-')}</div>
                  {nfId != null && (
                    <IconButton size="small" title="Visualizar Nota Fiscal" onClick={() => setNfModalOpen(true)}>
                      <i className="ri-file-search-line font-size-18" />
                    </IconButton>
                  )}
                </div>
              </div>

              {!nfId && <div style={{ fontSize: 12, color: '#9aa3ad' }}>Sem nota fiscal vinculada.</div>}
              {nfError && <div style={{ color: '#dc3545' }}>{nfError}</div>}
              {docsError && <div style={{ color: '#dc3545' }}>{docsError}</div>}

              {nfId != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>XML</div>
                    {docsLoading ? (
                      <Spinner size="sm" />
                    ) : xmlDoc?.arquivo_caminho ? (
                      <IconButton size="small" title="Abrir XML" onClick={() => openDoc(xmlDoc)}>
                        <i className="ri-folder-2-line font-size-18" />
                      </IconButton>
                    ) : (
                      <span style={{ fontSize: 12, color: '#9aa3ad' }}>Sem anexo</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>PDF</div>
                    {docsLoading ? (
                      <Spinner size="sm" />
                    ) : pdfDoc?.arquivo_caminho ? (
                      <IconButton size="small" title="Abrir PDF" onClick={() => openDoc(pdfDoc)}>
                        <i className="ri-folder-2-line font-size-18" />
                      </IconButton>
                    ) : (
                      <span style={{ fontSize: 12, color: '#9aa3ad' }}>Sem anexo</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Dialog open={nfModalOpen} onClose={() => setNfModalOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>{nf ? `Nota Fiscal ${nf.nf_num || ''} (ID: ${nf.nf_id})` : `Nota Fiscal (nf_id: ${rec?.nf_id})`}</DialogTitle>
              <DialogContent dividers>
                {nfLoading && (
                  <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                    <Spinner />
                  </Box>
                )}
                {!nfLoading && nfError && (
                  <Typography color="error" variant="body2">
                    {nfError}
                  </Typography>
                )}
                {!nfLoading && !nfError && nf && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="ID" value={nf.nf_id} />
                      <LabelValue label="Número" value={nf.nf_num || '-'} />
                      <LabelValue label="Status" value={nf.nf_status || '-'} />
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="Emissão" value={formatDateTime(nf.nf_dtEmissao)} />
                      <LabelValue label="Competência" value={formatDateOnly(nf.nf_comp)} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Descrição
                      </Typography>
                      <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, padding: 1.5, whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 240, overflow: 'auto' }}>
                        {nf.nf_desc || '-'}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Valores
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="Serviços" value={formatMoney(nf.nf_vlrServicos)} />
                        <LabelValue label="Base" value={formatMoney(nf.nf_baseCalculo)} />
                        <LabelValue label="Líquido" value={formatMoney(nf.nf_vlrLiq)} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Tributos (R$)
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="ISS" value={formatMoney(nf.nf_vlrISS)} />
                        <LabelValue label="ISS Ret." value={formatMoney(nf.nf_retISS)} />
                        <LabelValue label="Alíquota" value={formatPercent(nf.nf_aliq)} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setNfModalOpen(false)}>Fechar</Button>
              </DialogActions>
            </Dialog>
            </>
            )}
          </div>
        }
      />
    </div>
  );
};

export default function RecebimentosDashboard({ recs, mesRef, currentView, setCurrentView, selectedRecId, setSelectedRecId }) {
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [itemsInTable, setItemsInTable] = useState([]);
  const [title, setTitle] = useState('Recebimentos do mês');
  const [showBackButton, setShowBackButton] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);

  const today = useMemo(() => startOfToday(), []);
  const { start: mesStart, endExclusive: mesEndExclusive } = useMemo(() => monthRefToRange(mesRef), [mesRef]);

  const views = useMemo(() => {
    const withQuit = (recs || []).map((r) => ({ r, dtQuit: parseDate(r.rec_dtQuitAtual) }));

    const recebidosMes = withQuit
      .filter((x) => x.dtQuit && inRange(x.dtQuit, mesStart, mesEndExclusive) && isRecebido(x.r))
      .map((x) => x.r);

    const previstoMes = withQuit
      .filter((x) => x.dtQuit && inRange(x.dtQuit, mesStart, mesEndExclusive) && !isRecebido(x.r))
      .map((x) => x.r);

    const aFaturarMes = withQuit
      .filter((x) => x.dtQuit && inRange(x.dtQuit, mesStart, mesEndExclusive) && !isFaturado(x.r))
      .map((x) => x.r);

    const aberto = withQuit
      .filter((x) => {
        const dtQuit = x.dtQuit;
        if (!dtQuit || !inadimplenciaCutoff || dtQuit < inadimplenciaCutoff) return false;
        // Em aberto no prazo: somente faturados e ainda não quitados
        return isFaturado(x.r) && !isRecebido(x.r) && getRecebimentoDueDate(x.r) && !isRecebimentoEmAtraso(x.r, today);
      })
      .map((x) => x.r);

    const atraso = withQuit
      .filter((x) => {
        const dtQuit = x.dtQuit;
        if (!dtQuit || !inadimplenciaCutoff || dtQuit < inadimplenciaCutoff) return false;
        // Inadimplência: somente faturados e ainda não quitados
        return isFaturado(x.r) && !isRecebido(x.r) && getRecebimentoDueDate(x.r) && isRecebimentoEmAtraso(x.r, today);
      })
      .map((x) => x.r);

    return { recebidosMes, previstoMes, aFaturarMes, aberto, atraso };
  }, [recs, mesStart, mesEndExclusive, today]);

  useEffect(() => {
    setDataIsLoading(true);
    try {
      const viewMap = {
        recebidos_mes: views.recebidosMes,
        previsto_mes: views.previstoMes,
        a_faturar_mes: views.aFaturarMes,
        aberto: views.aberto,
        atraso: views.atraso,
      };
      const titleMap = {
        recebidos_mes: 'Recebidos no mês',
        previsto_mes: 'Receita líquida prevista no mês',
        a_faturar_mes: 'A faturar no mês',
        aberto: 'Em aberto no prazo',
        atraso: 'Em atraso',
      };
      const next = viewMap[currentView] ? currentView : 'recebidos_mes';
      setItemsInTable(viewMap[next] || views.recebidosMes);
      setTitle(titleMap[next] || 'Recebidos no mês');
      setShowBackButton(next !== 'recebidos_mes');

      if (selectedRecId != null) {
        const updated = (recs || []).find((r) => Number(r?.rec_id) === Number(selectedRecId));
        if (updated) {
          setSelectedRec(updated);
          setShowDetails(true);
        }
      }
    } finally {
      setDataIsLoading(false);
    }
  }, [recs, views, currentView, selectedRecId]);

  const columns = useMemo(() => {
    const YesNoDot = ({ value, trueColor = '#1cbb8c', falseColor = '#ff5757' }) => {
      const backgroundColor = value ? trueColor : falseColor;
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor, borderRadius: '50%', width: '12px', height: '12px' }} />
        </div>
      );
    };
    return [
      { accessorKey: 'rec_ep', header: 'EP', size: 200 },
      { accessorKey: 'recProjeto', header: 'Projeto', size: 240 },
      {
        id: 'situacao',
        header: 'Situação',
        accessorFn: (row) => getRecebSituacao(row, today),
        Cell: ({ row }) => <Pill value={getRecebSituacao(row.original, today)} />,
        size: 120,
      },
      {
        accessorKey: 'rec_dtVenc',
        header: 'Vencimento',
        sortingFn: 'datetime',
        accessorFn: (row) => getRecebimentoDueDate(row),
        Cell: ({ row }) => fmtDateBr(row.original.rec_dtVenc || row.original.rec_dtQuitAtual),
        size: 140,
      },
      {
        id: 'diasAtraso',
        header: 'Dias (atraso)',
        accessorFn: (row) => {
          return getDiasAtraso(row, today);
        },
        Cell: ({ row }) => {
          const d = getDiasAtraso(row.original, today);
          if (d == null) return '-';
          const color = !isRecebido(row.original) && d > 0 ? '#ff5757' : '#6c757d';
          return <span style={{ fontWeight: 800, color }}>{d}</span>;
        },
        size: 120,
      },
      { accessorKey: 'rec_vlrLiq', header: 'Valor líquido', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrLiq)), size: 130 },
      { accessorKey: 'rec_vlrBru', header: 'Valor bruto', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrBru)), size: 130 },
      {
        accessorKey: 'rec_fat',
        header: 'Faturado',
        Cell: ({ row }) => <YesNoDot value={isFaturado(row.original)} trueColor="#5664d2" falseColor="#ff5757" />,
        size: 90,
      },
      { accessorKey: 'rec_risco', header: 'Risco', size: 80 },
      { accessorKey: 'rec_id', header: 'ID', size: 80 },
    ];
  }, [today]);

  if (dataIsLoading) {
    return (
      <div className="centered">
        <Spinner />
      </div>
    );
  }

  return (
    <Container fluid>
      <h3 style={{ marginBottom: 16 }}>Recebimentos / Inadimplência</h3>
      <div
        style={{
          marginBottom: 14,
          padding: '10px 12px',
          borderRadius: 6,
          border: '1px solid #f1b44c',
          backgroundColor: 'rgba(241,180,76,0.14)',
          color: '#7a5600',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        Módulo em desenvolvimento: indicadores e valores de recebimentos são ilustrativos e ainda precisam de validação.
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <MetricCard
          title="Recebidos no mês"
          recs={views.recebidosMes}
          className="mr-4"
          selected={currentView === 'recebidos_mes'}
          onClick={() => {
            setCurrentView('recebidos_mes');
            setItemsInTable(views.recebidosMes);
            setTitle('Recebidos no mês');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Em aberto no prazo"
          recs={views.aberto}
          className="mr-4"
          selected={currentView === 'aberto'}
          onClick={() => {
            setCurrentView('aberto');
            setItemsInTable(views.aberto);
            setTitle('Em aberto no prazo');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Em atraso"
          recs={views.atraso}
          className="mr-4"
          selected={currentView === 'atraso'}
          onClick={() => {
            setCurrentView('atraso');
            setItemsInTable(views.atraso);
            setTitle('Em atraso');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="A faturar no mês"
          recs={views.aFaturarMes}
          selected={currentView === 'a_faturar_mes'}
          onClick={() => {
            setCurrentView('a_faturar_mes');
            setItemsInTable(views.aFaturarMes);
            setTitle('A faturar no mês');
            setShowBackButton(true);
          }}
        />
      </div>

      <div className="d-flex mt-3">
        <div className="d-flex flex-column" style={{ width: '50%' }}>
          <SimpleCard
            standard={false}
            title={<h4 className="text-truncate font-size-14 mb-2">{title}</h4>}
            text={
              <MaterialReactTable
                columns={columns}
                data={itemsInTable}
                initialState={{
                  density: 'compact',
                  pagination: { pageIndex: 0, pageSize: 10 },
                  sorting: [{ id: 'diasAtraso', desc: true }],
                }}
                enableColumnResizing
                enableGrouping={false}
                enableGlobalFilter
                enableFilters
                enablePagination={itemsInTable.length > 10}
                muiTableBodyCellProps={{ sx: { fontSize: '10px' } }}
                muiTablePaginationProps={{
                  sx: {
                    '.MuiTablePagination-displayedRows': { fontSize: '12px' },
                    marginTop: '0px',
                    fontSize: '12px',
                  },
                  labelRowsPerPage: <span style={{ fontSize: '12px' }}>Itens por página</span>,
                  rowsPerPageOptions: [10, 50, 100],
                }}
                positionPagination="bottom"
                enableDensityToggle={false}
                enableColumnFilters
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => {
                    setShowDetails(true);
                    setSelectedRec(row.original);
                    setSelectedRecId(row.original?.rec_id ?? null);
                  },
                  sx: (() => {
                    const r = row.original;
                    const d = getDiasAtraso(r, today);
                    let bg = undefined;
                    if (!isRecebido(r) && d != null && d > 0) bg = 'rgba(255,87,87,0.12)';
                    else if (isRecebido(r)) bg = 'rgba(28,187,140,0.10)';
                    return { cursor: 'pointer', backgroundColor: bg };
                  })(),
                })}
              />
            }
          />
        </div>

        <div className="d-flex flex-column ml-2 mr-4" style={{ width: '50%' }}>
          {showDetails && <DetailsCard rec={selectedRec} />}
        </div>
      </div>
    </Container>
  );
}

